import React, { useState, useEffect } from 'react';
import { 
  WalletIcon, 
  XMarkIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import TransactionModal from './TransactionModal';
import { getSelfUserType, fetchSelf } from '../services/userSelf';
import { notificationService } from '../services/notificationService';
import { subscribeToBalanceUpdates } from '../services/userTreeService';
import useTranslation from '../services/translationService';

const WalletModal = ({ isOpen, onClose, user }) => {
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [balances, setBalances] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const { t } = useTranslation();

  // Subscribe to balance updates
  useEffect(() => {
    if (!isOpen || !user?.username) return;

    const cleanup = subscribeToBalanceUpdates((username, newBalances) => {
      // Only update if this is our user
      if (username === user.username) {
        const formattedBalances = Object.entries(newBalances).map(([currency, amount]) => ({
          currency,
          amount
        }));
        setBalances(formattedBalances);
        console.log('Wallet modal: Updated balances from subscription:', formattedBalances);
      }
    });

    return cleanup;
  }, [isOpen, user?.username]);

  // Add notification handler
  useEffect(() => {
    if (!isOpen) return;

    // Register for transaction notifications
    const cleanup = notificationService.onNotification((message) => {
      if (message.content && (
        message.content.includes('Transaction:') || 
        message.content.includes('deposit') || 
        message.content.includes('withdraw') ||
        message.content.includes('balance'))) {
        console.log('Wallet modal: Transaction notification received, refreshing data');
        fetchUserData();
      }
    });

    return cleanup;
  }, [isOpen]);

  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      const userData = await fetchSelf();
      console.log('Wallet modal: Fetched user data:', userData);
      
      if (userData?.balances) {
        const formattedBalances = Object.entries(userData.balances).map(([currency, amount]) => ({
          currency,
          amount
        }));
        setBalances(formattedBalances);
        console.log('Wallet modal: Updated balances:', formattedBalances);
      }
    } catch (error) {
      console.error('Error fetching user balances:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkUserType = async () => {
      try {
        const userType = await getSelfUserType();
        setIsAdmin(userType?.toLowerCase() === 'admin');
      } catch (error) {
        console.error('Error checking user type:', error);
        setIsAdmin(false);
      }
    };
    checkUserType();
  }, []);

  // Fetch latest user data when modal opens
  useEffect(() => {
    if (!isOpen || !user?.username) return;
    fetchUserData();
  }, [isOpen, user?.username]);

  const formatBalance = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[100]" onClick={onClose} />
      <div className="fixed end-2 top-[calc(4rem+env(safe-area-inset-top))] z-[101] w-80 rounded-xl bg-white dark:bg-gray-800 shadow-xl">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <WalletIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t('wallet.title')}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label={t('common.close')}
            >
              <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            </div>
          ) : balances.length > 0 ? (
            balances.map(({ currency, amount }) => (
              <div 
                key={currency}
                className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50"
              >
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  {currency}
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {formatBalance(amount, currency)}
                </span>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {t('wallet.noBalances', 'No balances available')}
            </div>
          )}
        </div>

        {console.log('Full user object:', user)}
        {console.log('User attributes:', user?.attributes)}
        {isAdmin && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowDepositModal(true)}
              className="flex items-center justify-center w-full gap-2 px-4 py-2 
                bg-teal-500 hover:bg-teal-600 dark:bg-teal-600 dark:hover:bg-teal-700
                text-white rounded-lg transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              <span>{t('wallet.deposit')}</span>
            </button>
          </div>
        )}
      </div>

      <TransactionModal
        isOpen={showDepositModal}
        onClose={() => setShowDepositModal(false)}
        type="deposit"
        preselectedUser={{
          id: user?.id,
          name: user?.username,
          username: user?.username,
          type: user?.type,
          balances: balances.reduce((acc, { currency, amount }) => {
            acc[currency] = amount;
            return acc;
          }, {})
        }}
      />
    </>
  );
};

export default WalletModal; 